import {Component, OnDestroy, ChangeDetectionStrategy} from "@angular/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {NzTableFilterList, NzTableQueryParams, NzTableSortOrder} from "ng-zorro-antd/table";
import {lastValueFrom} from "rxjs";
import {AuthService} from "src/app/services/auth.service";
import {PerFormaAPIService, ScanDto, ScanQueryDto, ScanResultsDto} from "src/app/services/pfapi.service";

export interface SortDto {
    key: string;
    value: NzTableSortOrder;
}

@Component({
    selector: "app-scan-list-tab",
    templateUrl: "./scan-list-tab.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ScanListTabComponent implements OnDestroy {
    constructor(
        private api: PerFormaAPIService,
        private msg: NzMessageService,
        public auth: AuthService
    ) {}

    scansList: ScanResultsDto = {Items: []};
    // object URLs for the authenticated scan-image fetches, keyed by ScanID. the scan-image endpoint
    // requires a Bearer token, so we fetch the blobs via the API client (not a raw <img src>).
    // thumbUrls = small "mini" variant shown in the grid; previewUrls = larger "preview" variant shown in the hover popover.
    thumbUrls: Record<number, string> = {};
    previewUrls: Record<number, string> = {};
    loading = true;
    total = 0;
    pageIndex = 1;
    pageSize = 10;
    pageSizeOptions = [5, 10, 20, 50];
    searchValue = "";
    lastParams: NzTableQueryParams | null = null;
    activeFilters: Record<string, string[]> = {};
    defaultSort: SortDto[] = [
        {key: "ScanID", value: null},
        {key: "DocumentType", value: null},
        {key: "Timestamp", value: "descend"},
        {key: "UserLock", value: null}
    ];
    sort: SortDto[] = [...this.defaultSort];
    docTypeFilters: NzTableFilterList = [];
    userLockFilters: NzTableFilterList = [];
    columns = [
        {
            title: "Preview",
            compare: null,
            fieldName: null,
            align: "center" as const,
            width: "100px"
        },
        {
            title: "Document Type",
            compare: (a: ScanDto, b: ScanDto) => (a.DocumentType ?? "").localeCompare(b.DocumentType ?? ""),
            fieldName: "DocumentType",
            align: null,
            width: null
        },
        {
            title: "Timestamp",
            compare: (a: ScanDto, b: ScanDto) => (a.Timestamp ?? "").localeCompare(b.Timestamp ?? ""),
            fieldName: "Timestamp",
            align: null,
            width: null
        },
        {
            title: "Locked By",
            compare: (a: ScanDto, b: ScanDto) => (a.UserLock ?? "").localeCompare(b.UserLock ?? ""),
            fieldName: "UserLock",
            align: null,
            width: null
        },
        {
            title: "Actions",
            compare: null,
            fieldName: null,
            align: "center" as const,
            width: "80px"
        }
    ];

    onSearch = async () => {
        this.pageIndex = 1;
        await this.getData();
    };

    clearSearch = async () => {
        this.searchValue = "";
        this.pageIndex = 1;
        await this.getData();
    };

    canViewScan = scan => {
        const status = scan.ScanStatus;
        const lockedBy = scan.UserLock;
        return status == 2 && (!lockedBy || lockedBy == this.auth.username);
    };

    getData = async () => {
        this.loading = true;
        const params: ScanQueryDto = {
            Page: this.pageIndex,
            PageSize: this.pageSize
        };

        const activeSort = this.sort?.filter(s => s.value !== null) || [...this.defaultSort];
        if (activeSort.length) {
            params.SortField = activeSort[0].key;
            params.SortOrder = activeSort[0].value;
        }

        if (this.searchValue) {
            params.SearchValue = this.searchValue;
        }
        if (this.activeFilters["DocumentType"]?.length) {
            params.DocumentTypeFilter = this.activeFilters["DocumentType"];
        }
        if (this.activeFilters["UserLock"]?.length) {
            params.UserLockFilter = this.activeFilters["UserLock"];
        }

        try {
            this.scansList = await lastValueFrom(this.api.postApiScanScans(params));
            this.total = this.scansList.TotalItems ?? 0;
            this.buildFilterOptions(this.scansList);
            await this.loadPreviewImages(this.scansList.Items ?? []);
        } catch (error) {
            this.msg.error("Failed to load scans");
            console.log(error);
        } finally {
            this.loading = false;
        }
    };

    // fetches the mini (thumbnail) and preview (hover-zoom) scan images through the authenticated
    // client and exposes them as object URLs. previous URLs are revoked to avoid leaks.
    private loadPreviewImages = async (scans: ScanDto[]) => {
        this.revokePreviewUrls();
        await Promise.all(
            scans
                .filter(s => s.ScanStatus === 2)
                .map(async scan => {
                    const id = scan.ScanID;
                    try {
                        const [mini, preview] = await Promise.all([
                            lastValueFrom(this.api.getApiScanImage("image/jpeg", {scanId: id, mini: true})),
                            lastValueFrom(this.api.getApiScanImage("image/jpeg", {scanId: id, preview: true}))
                        ]);
                        this.thumbUrls[id] = URL.createObjectURL(mini);
                        this.previewUrls[id] = URL.createObjectURL(preview);
                    } catch {
                        // a missing pre-rendered variant (404) leaves the thumbnail blank — non-fatal.
                    }
                })
        );
    };

    private revokePreviewUrls(): void {
        Object.values(this.thumbUrls).forEach(url => URL.revokeObjectURL(url));
        Object.values(this.previewUrls).forEach(url => URL.revokeObjectURL(url));
        this.thumbUrls = {};
        this.previewUrls = {};
    }

    ngOnDestroy(): void {
        this.revokePreviewUrls();
    }

    buildFilterOptions(results: ScanResultsDto) {
        const activeDocTypes = new Set(this.activeFilters["DocumentType"] ?? []);
        const docTypes = results.DocumentTypes ?? [];
        this.docTypeFilters = docTypes.map(dt => ({text: dt, value: dt, byDefault: activeDocTypes.has(dt)}));

        const activeUserLocks = new Set(this.activeFilters["UserLock"] ?? []);
        const users = results.UserLocks ?? [];
        this.userLockFilters = [
            {text: "Unlocked", value: "Unlocked", byDefault: activeUserLocks.has("Unlocked")},
            ...users.map(u => ({text: u, value: u, byDefault: activeUserLocks.has(u)}))
        ];
    }

    unlockScan = async scanID => {
        this.loading = true;
        try {
            const resp = await lastValueFrom(this.api.postApiScanScanIdUnlock(scanID));
            this.msg.success(resp);
            await this.getData();
        } catch (e) {
            this.msg.error(e.error);
        } finally {
            this.loading = false;
        }
    };

    deleteScan = async scanID => {
        this.loading = true;
        try {
            const resp = await lastValueFrom(this.api.deleteApiScanScanId(scanID));
            this.msg.success(resp);
            await this.getData();
        } catch (e) {
            this.msg.error(e.error);
        } finally {
            this.loading = false;
        }
    };

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        if (this.isParamsSame(this.lastParams, params)) {
            return;
        }

        this.lastParams = {...params};
        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;

        if (this.sortChanged(params.sort)) {
            this.pageIndex = 1;
        }

        const newFilters: Record<string, string[]> = {};
        for (const f of params.filter) {
            if (f.value?.length) {
                newFilters[f.key] = f.value;
            }
        }
        if (this.filtersChanged(newFilters)) {
            this.pageIndex = 1;
        }
        this.activeFilters = newFilters;

        this.sort = [...params.sort];
        await this.getData();
    }

    filtersChanged(newFilters: Record<string, string[]>): boolean {
        const keys = new Set([...Object.keys(this.activeFilters), ...Object.keys(newFilters)]);
        for (const key of keys) {
            const oldVal = this.activeFilters[key] ?? [];
            const newVal = newFilters[key] ?? [];
            if (oldVal.length !== newVal.length || !oldVal.every((v, i) => v === newVal[i])) {
                return true;
            }
        }
        return false;
    }

    isParamsSame(a: NzTableQueryParams | null, b: NzTableQueryParams): boolean {
        if (!a) return false;
        const activeSortA = a.sort.filter(s => s.value !== null);
        const activeSortB = b.sort.filter(s => s.value !== null);
        const activeFilterA = a.filter.filter(f => f.value?.length);
        const activeFilterB = b.filter.filter(f => f.value?.length);
        return (
            a.pageIndex === b.pageIndex &&
            a.pageSize === b.pageSize &&
            activeSortA.length === activeSortB.length &&
            activeSortA.every((s, i) => s.key === activeSortB[i].key && s.value === activeSortB[i].value) &&
            activeFilterA.length === activeFilterB.length &&
            activeFilterA.every(
                (f, i) =>
                    f.key === activeFilterB[i].key && JSON.stringify(f.value) === JSON.stringify(activeFilterB[i].value)
            )
        );
    }

    sortChanged = (sort: SortDto[]): boolean => {
        const newSort = sort.filter(s => s.value !== null);
        const currentSort = this.sort
            ? this.sort.filter(s => s.value !== null)
            : [...this.defaultSort].filter(s => s.value !== null);

        if (newSort.length === 0 && currentSort.length === 0) {
            return false;
        }
        if (newSort.length !== currentSort.length) {
            return true;
        }

        const newActiveSort = newSort[0];
        const currentActiveSort = currentSort[0];
        return newActiveSort.key !== currentActiveSort.key || newActiveSort.value !== currentActiveSort.value;
    };

    getSortOrder(fieldName: string | null): NzTableSortOrder {
        if (!fieldName || !this.sort) {
            return null;
        }
        const sortItem = this.sort.find(s => s.key === fieldName);
        return sortItem ? sortItem.value : null;
    }
}
